import React, { useState, useRef, useEffect } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  Line,
  Annotation
} from 'react-simple-maps';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, LabelList } from 'recharts';

const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Coordinates for key locations
const locations = {
  angola: { coordinates: [17.8739, -11.2027], name: 'Angola (ABC)' },
  belgium: { coordinates: [4.3517, 50.8503], name: { en: 'Belgium', pt: 'Bélgica' } },
  uae: { coordinates: [55.2708, 25.2048], name: { en: 'UAE', pt: 'EAU' } },
  israel: { coordinates: [34.8516, 31.0461], name: { en: 'Israel', pt: 'Israel' } },
  india: { coordinates: [78.9629, 20.5937], name: { en: 'India', pt: 'Índia' } },
};

// Quarterly revenue data by country
const quarterlyRevenueData = {
  belgium: [
    { quarter: '1T', y2024: 28.5, y2025: 32.0 },
    { quarter: '2T', y2024: 31.2, y2025: 34.5 },
    { quarter: '3T', y2024: 33.0, y2025: 31.8 },
    { quarter: '4T', y2024: 35.8, y2025: 32.7 },
  ],
  israel: [
    { quarter: '1T', y2024: 10.2, y2025: 11.5 },
    { quarter: '2T', y2024: 11.5, y2025: 12.8 },
    { quarter: '3T', y2024: 12.0, y2025: 11.5 },
    { quarter: '4T', y2024: 11.8, y2025: 12.2 },
  ],
  uae: [
    { quarter: '1T', y2024: 15.0, y2025: 17.2 },
    { quarter: '2T', y2024: 16.5, y2025: 18.5 },
    { quarter: '3T', y2024: 17.2, y2025: 17.8 },
    { quarter: '4T', y2024: 18.8, y2025: 17.5 },
  ],
  india: [
    { quarter: '1T', y2024: 6.8, y2025: 8.0 },
    { quarter: '2T', y2024: 7.5, y2025: 8.5 },
    { quarter: '3T', y2024: 8.2, y2025: 8.2 },
    { quarter: '4T', y2024: 9.0, y2025: 8.3 },
  ],
};

// HTML overlay positions (percentage based) - positioned above country markers
const overlayPositions = {
  belgium: { left: '25%', top: '5%' },
  israel: { left: '42%', top: '22%' },
  uae: { left: '62%', top: '28%' },
  india: { left: '82%', top: '33%' },
};

const RevenueWorldMap = ({ 
  data, 
  language,
  hoveredCountry: externalHoveredCountry,
  hoveredClient,
  selectedCountry,
  onCountryHover,
  onCountryClick,
  isCountryHighlighted,
  getCountryByClient
}) => {
  const [internalHoveredCountry, setInternalHoveredCountry] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  
  // Use external hover state if provided, otherwise use internal
  const hoveredCountry = externalHoveredCountry !== undefined ? externalHoveredCountry : internalHoveredCountry;
  
  // Determine active country (from client hover)
  const activeCountryFromClient = hoveredClient && getCountryByClient ? getCountryByClient(hoveredClient) : null;

  const handleMouseEnter = (countryId, event) => {
    if (onCountryHover) {
      onCountryHover(countryId);
    } else {
      setInternalHoveredCountry(countryId);
    }
  };

  const handleMouseLeave = () => {
    if (onCountryHover) {
      onCountryHover(null);
    } else {
      setInternalHoveredCountry(null);
    }
  };
  
  const handleClick = (countryId) => {
    if (onCountryClick) {
      onCountryClick(countryId);
    }
  };
  
  // Check if a country should be highlighted
  const checkHighlighted = (countryId) => {
    if (isCountryHighlighted) {
      return isCountryHighlighted(countryId);
    }
    return true;
  };

  // Map data to locations
  const destinations = [
    { 
      id: 'belgium',
      coordinates: locations.belgium.coordinates,
      name: locations.belgium.name,
      value: data[0]?.value || 131, 
      percentage: data[0]?.percentage || 44,
      color: '#0A192F',
    },
    { 
      id: 'israel',
      coordinates: locations.israel.coordinates,
      name: locations.israel.name,
      value: data[2]?.value || 48, 
      percentage: data[2]?.percentage || 16.1,
      color: '#7C3AED',
    },
    { 
      id: 'uae',
      coordinates: locations.uae.coordinates,
      name: locations.uae.name,
      value: data[1]?.value || 71, 
      percentage: data[1]?.percentage || 23.8,
      color: '#C5A059',
    },
    { 
      id: 'india',
      coordinates: locations.india.coordinates,
      name: locations.india.name,
      value: data[3]?.value || 33, 
      percentage: data[3]?.percentage || 11.1,
      color: '#3B82F6',
    },
  ];

  const source = locations.angola;

  const getHoveredDestination = () => {
    return destinations.find(d => d.id === hoveredCountry);
  };

  return (
    <div className="relative" ref={containerRef}>
      {/* Map Container */}
      <div className="map-container bg-slate-50 rounded-xl overflow-hidden border border-slate-200 relative" style={{ isolation: 'isolate' }}>
        <ComposableMap
          projection="geoMercator"
          projectionConfig={{
            scale: 320,
            center: [45, 25]
          }}
          style={{ width: '100%', height: '360px' }}
        >
          {/* World Map */}
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const isAngola = geo.properties.ISO_A3 === 'AGO';
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={isAngola ? '#C5A059' : '#D1D5DB'}
                    stroke="#9CA3AF"
                    strokeWidth={0.5}
                    style={{
                      default: { outline: 'none' },
                      hover: { outline: 'none' },
                      pressed: { outline: 'none' },
                    }}
                  />
                );
              })
            }
          </Geographies>

          {/* Connection Lines */}
          {destinations.map((dest) => {
            const isHighlighted = checkHighlighted(dest.id);
            const isActive = activeCountryFromClient === dest.id || selectedCountry === dest.id || hoveredCountry === dest.id;
            
            return (
              <Line
                key={dest.id}
                from={source.coordinates}
                to={dest.coordinates}
                stroke={dest.color}
                strokeWidth={isActive ? 4 : 2.5}
                strokeLinecap="round"
                strokeOpacity={isHighlighted ? (isActive ? 1 : 0.8) : 0.2}
                style={{ transition: 'all 0.2s ease' }}
              />
            );
          })}

          {/* Source Marker - Angola */}
          <Marker coordinates={source.coordinates}>
            <circle r={14} fill="#C5A059" stroke="#FFF" strokeWidth={3} />
            <circle r={6} fill="#FFF" />
          </Marker>

          {/* Destination Markers */}
          {destinations.map((dest) => {
            const isHighlighted = checkHighlighted(dest.id);
            const isActive = activeCountryFromClient === dest.id || selectedCountry === dest.id || hoveredCountry === dest.id;
            
            return (
              <Marker key={dest.id} coordinates={dest.coordinates}>
                <circle 
                  r={isActive ? 12 : 8} 
                  fill={dest.color} 
                  stroke="#FFF" 
                  strokeWidth={isActive ? 3 : 2}
                  opacity={isHighlighted ? 1 : 0.3}
                  style={{ transition: 'all 0.2s ease', cursor: 'pointer' }}
                />
              </Marker>
            );
          })}

          {/* Angola Label */}
          <Annotation
            subject={source.coordinates}
            dx={-15}
            dy={30}
            connectorProps={{ stroke: 'transparent' }}
          >
            <text
              textAnchor="middle"
              alignmentBaseline="middle"
              style={{ fontFamily: 'system-ui', fontSize: '14px', fontWeight: '600', fill: '#64748B' }}
            >
              ABC
            </text>
          </Annotation>
        </ComposableMap>

        {/* HTML Overlay Cards for each country */}
        <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 10 }}>
          {destinations.map((dest) => {
            const countryName = dest.name[language] || dest.name.en;
            const pos = overlayPositions[dest.id];
            const isHighlighted = checkHighlighted(dest.id);
            const isActive = activeCountryFromClient === dest.id || selectedCountry === dest.id;
            const isHovered = hoveredCountry === dest.id;
            
            return (
              <div
                key={dest.id}
                data-testid={`country-card-${dest.id}`}
                className="absolute cursor-pointer transform -translate-x-1/2 pointer-events-auto"
                style={{ 
                  left: pos.left, 
                  top: pos.top,
                  opacity: isHighlighted ? 1 : 0.3,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => handleMouseEnter(dest.id, e)}
                onMouseLeave={handleMouseLeave}
                onClick={() => handleClick(dest.id)}
              >
                {/* Card - Compact size */}
                <div 
                  className="bg-white rounded-lg shadow-sm px-2 py-1.5 text-center transition-all duration-200"
                  style={{ 
                    borderWidth: isActive ? '3px' : isHovered ? '2px' : '2px',
                    borderStyle: 'solid',
                    borderColor: isActive ? dest.color : dest.color,
                    minWidth: '70px',
                    maxWidth: '80px',
                    transform: isActive ? 'scale(1.2)' : (isHovered ? 'scale(1.1)' : 'scale(1)'),
                    boxShadow: isActive 
                      ? `0 8px 24px ${dest.color}50` 
                      : isHovered 
                        ? '0 4px 12px rgba(0,0,0,0.15)' 
                        : '0 1px 3px rgba(0,0,0,0.1)',
                    backgroundColor: isActive ? `${dest.color}15` : '#fff',
                    zIndex: isActive ? 20 : 1
                  }}
                >
                  <p className="text-[10px] text-slate-500 font-medium leading-tight">{countryName}</p>
                  <p className="text-sm font-bold leading-tight" style={{ color: dest.color }}>${dest.value}M</p>
                  <p className="text-[8px] text-slate-400 leading-tight">{dest.percentage}%</p>
                </div>
                {/* Connector line (visual only) */}
                <div 
                  className="w-0.5 h-5 mx-auto"
                  style={{ 
                    background: `linear-gradient(to bottom, ${dest.color}, transparent)`,
                    marginTop: '-1px'
                  }}
                />
              </div>
            );
          })}
        </div>

        {/* Hover Popup with Quarterly Chart - with smooth animation */}
        <div 
          data-testid={hoveredCountry ? `quarterly-popup-${hoveredCountry}` : undefined}
          className={`absolute bg-white rounded-lg shadow-xl border-2 p-3 pointer-events-none transition-all duration-300 ease-out ${
            hoveredCountry && quarterlyRevenueData[hoveredCountry] 
              ? 'opacity-100 translate-y-0 scale-100' 
              : 'opacity-0 translate-y-2 scale-95'
          }`}
          style={{
            right: '8px',
            bottom: '8px',
            borderColor: getHoveredDestination()?.color || '#0A192F',
            width: '220px',
            zIndex: 50,
            visibility: hoveredCountry && quarterlyRevenueData[hoveredCountry] ? 'visible' : 'hidden',
          }}
        >
          {hoveredCountry && quarterlyRevenueData[hoveredCountry] && (
            <>
              {/* Header */}
              <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-100">
                <div 
                  className="w-2.5 h-2.5 rounded-full" 
                  style={{ backgroundColor: getHoveredDestination()?.color }}
                />
                <span className="font-semibold text-sm text-slate-700">
                  {getHoveredDestination()?.name[language] || getHoveredDestination()?.name.en}
                </span>
              </div>
              
              {/* Bar Chart - 30% larger */}
              <div className="h-[104px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={quarterlyRevenueData[hoveredCountry]} barGap={2}>
                    <XAxis 
                      dataKey="quarter" 
                      tick={{ fontSize: 10, fill: '#64748B' }} 
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis 
                      tick={{ fontSize: 9, fill: '#94A3B8' }} 
                      axisLine={false}
                      tickLine={false}
                      width={22}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1E293B',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '11px',
                        padding: '4px 8px',
                      }}
                      labelStyle={{ color: '#FFF', fontWeight: 'bold', fontSize: '11px' }}
                      itemStyle={{ color: '#FFF', fontSize: '11px' }}
                      formatter={(value) => [`$${value}M`, '']}
                    />
                    <Bar dataKey="y2024" fill="#CBD5E1" radius={[2, 2, 0, 0]} name="2024">
                      <LabelList 
                        dataKey="y2024" 
                        position="top" 
                        style={{ fontSize: '8px', fill: '#64748B', fontWeight: '500' }}
                        formatter={(value) => value}
                      />
                    </Bar>
                    <Bar dataKey="y2025" fill={getHoveredDestination()?.color || '#0A192F'} radius={[2, 2, 0, 0]} name="2025">
                      <LabelList 
                        dataKey="y2025" 
                        position="top" 
                        style={{ fontSize: '8px', fill: '#0E1633', fontWeight: '600' }}
                        formatter={(value) => value}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              
              {/* Legend - Compact */}
              <div className="flex justify-center gap-2.5 mt-1">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded bg-slate-300"></div>
                  <span className="text-slate-500 text-[9px]">2024</span>
                </div>
                <div className="flex items-center gap-1">
                  <div 
                    className="w-2 h-2 rounded" 
                    style={{ backgroundColor: getHoveredDestination()?.color }}
                  ></div>
                  <span className="text-slate-500 text-[9px]">2025</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RevenueWorldMap;
